import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, LOGO_GROUP, CONTACT } from "@/data/content";

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setOpen(false), [location.pathname]);

    return (
        <>
            <header
                data-testid="main-navbar"
                className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                    scrolled ? "border-b border-white/10 bg-[#0A0D20]/85 backdrop-blur-xl" : "bg-transparent"
                }`}
            >
                <div className="mx-auto flex h-24 max-w-[1440px] items-center justify-between px-6 md:h-28 md:px-12 lg:px-20">
                    <Link to="/" data-testid="nav-logo" className="inline-block">
                        <img src="/assets/logo-ms-group-white.png" alt="MS Group" className="h-14 w-auto object-contain md:h-16" />
                    </Link>

                    <nav className="hidden items-center gap-10 lg:flex">
                        {NAV_LINKS.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                                className={({ isActive }) =>
                                    `text-[13px] font-medium uppercase tracking-[0.18em] transition-colors duration-300 ${
                                        isActive ? "text-[#F16622]" : "text-white/75 hover:text-white"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                    </nav>

                    <button
                        data-testid="nav-mobile-menu-btn"
                        onClick={() => setOpen(true)}
                        className="text-white lg:hidden"
                        aria-label="Open menu"
                    >
                        <Menu className="h-7 w-7" strokeWidth={1.5} />
                    </button>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.35 }}
                        className="fixed inset-0 z-[60] flex flex-col bg-[#0A0D20]"
                        data-testid="mobile-menu"
                    >
                        <div className="flex h-24 items-center justify-between px-6">
                            <span className="inline-block">
                                <img src="/assets/logo-ms-group-white.png" alt="MS Group" className="h-12 w-auto object-contain" />
                            </span>
                            <button onClick={() => setOpen(false)} data-testid="nav-mobile-close-btn" aria-label="Close menu" className="text-white">
                                <X className="h-7 w-7" strokeWidth={1.5} />
                            </button>
                        </div>
                        <nav className="flex flex-1 flex-col justify-center gap-2 px-8">
                            {NAV_LINKS.map((l, i) => (
                                <motion.div
                                    key={l.to}
                                    initial={{ opacity: 0, y: 24 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.1 + i * 0.08, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    <NavLink
                                        to={l.to}
                                        data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                                        className={({ isActive }) =>
                                            `font-display block py-3 text-4xl font-medium tracking-tight transition-colors ${
                                                isActive ? "text-[#F16622]" : "text-white hover:text-[#F16622]"
                                            }`
                                        }
                                    >
                                        {l.label}
                                    </NavLink>
                                </motion.div>
                            ))}
                        </nav>
                        <p className="px-8 pb-10 text-sm text-white/50">{CONTACT.email}</p>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
